import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/use-auth';
import { useLanguage } from '../../hooks/use-language';
import { usePermissions } from '../../hooks/use-permissions';
import { Link, useLocation } from "wouter";
import { 
  BuildingIcon, 
  LayoutDashboardIcon, 
  CalendarIcon, 
  UsersIcon, 
  FileTextIcon, 
  BanknoteIcon, 
  HammerIcon, 
  BotIcon, 
  SettingsIcon, 
  LogOutIcon,
  ClipboardCheckIcon,
  Globe,
  TrophyIcon,
  Clock,
  Palette,
  Settings,
  FileText,
  CreditCard,
  MessageSquare,
  LogOut,
  Calculator,
  Lock,
  Wrench
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { LanguageSwitcher } from '../language-switcher';
import { Separator } from '../ui/separator';
import { Button } from '../ui/button';
import RemodraLogo from '../remodra-logo';
import { Badge } from '../ui/badge';
import { ThemeToggle } from '../ui/theme-toggle';
import { authInterceptor } from '../../lib/auth-interceptor';

export default function Sidebar() {
  const [location] = useLocation();
  const { user, logoutMutation } = useAuth();
  const { t } = useLanguage();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  // Initialize collapsed state from localStorage / document on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const saved = localStorage.getItem('remodra:sidebar-collapsed');
      const shouldCollapse = saved === '1' || document.documentElement.classList.contains('sidebar-collapsed');
      if (shouldCollapse) {
        document.documentElement.classList.add('sidebar-collapsed');
        setIsCollapsed(true);
      }
    } catch {
      // ignore
    }
  }, []);

  // Keep document class + storage in sync when user toggles
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (isCollapsed) {
      document.documentElement.classList.add('sidebar-collapsed');
    } else {
      document.documentElement.classList.remove('sidebar-collapsed');
    }
    try {
      localStorage.setItem('remodra:sidebar-collapsed', isCollapsed ? '1' : '0');
    } catch {
      // ignore
    }
  }, [isCollapsed]);

  const isActivePath = (path: string) => {
    return location === path;
  };

  const getLinkClass = (path: string) => {
    return `flex items-center px-4 py-3 mx-2 my-1 rounded-xl transition-all duration-300 ease-in-out group ${
      isActivePath(path) 
        ? "bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-white border border-blue-400/30 shadow-lg backdrop-blur-sm" 
        : "text-slate-300 hover:bg-white/10 hover:text-white hover:shadow-md hover:scale-105 hover:translate-x-1"
    }`;
  };

  const getInitials = () => {
    if (!user) return "U";
    const firstName = typeof user.firstName === 'string' ? user.firstName : '';
    const lastName = typeof user.lastName === 'string' ? user.lastName : '';
    return `${firstName[0] || ""}${lastName[0] || ""}`;
  };

  // Removed admin detection - all users are treated equally

  // Plan-based access control — aligned with backend PLAN_FEATURES (api/lib/plan-access.js)
  const getPlanAccess = () => {
    let plan = user?.plan;
    if (!plan) {
      try {
        const localUser = localStorage.getItem('user');
        if (localUser) {
          const parsed = JSON.parse(localUser);
          plan = parsed?.plan;
        }
      } catch (e) {
        // ignore
      }
    }
    plan = (plan || 'basic').toString().toLowerCase().trim();

    // Match backend: Basic = no AI, no timeclock, no agents/workers; Pro = +timeclock +AI; Business = +agents +workers
    const planFeatures = {
      basic: ['dashboard', 'clients', 'estimates', 'invoices', 'services', 'settings'],
      pro: ['dashboard', 'clients', 'estimates', 'invoices', 'calendar', 'projects', 'services', 'timeclock', 'settings', 'ai-assistant'],
      business: ['dashboard', 'clients', 'estimates', 'invoices', 'calendar', 'projects', 'agents', 'services', 'timeclock', 'ai-assistant', 'materials', 'settings']
    };

    const planAccess = {
      basic: { features: planFeatures.basic, name: 'Basic Plan', color: 'bg-blue-500' },
      pro: { features: planFeatures.pro, name: 'Pro Plan', color: 'bg-green-500' },
      business: { features: planFeatures.business, name: 'Business Plan', color: 'bg-green-500' }
    };

    return planAccess[plan as keyof typeof planAccess] || planAccess.basic;
  };

  const planAccess = getPlanAccess();
  const { canAccessFeature } = usePermissions();
  const [showAiRoofMeasurement, setShowAiRoofMeasurement] = useState(false);

  // Load contractor settings when route changes or when settings are updated
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch('/api/protected/contractor/settings', {
          credentials: 'include',
          headers: authInterceptor.addAuthHeaders({ 'Content-Type': 'application/json' }),
        });
        if (!res.ok) return;
        const data = await res.json();
        setShowAiRoofMeasurement(Boolean((data as any).showAiRoofMeasurement));
      } catch (error) {
        console.error('Failed to load contractor settings for sidebar:', error);
      }
    };

    loadSettings();

    const handleSettingsUpdated = (event: any) => {
      const nextValue = event.detail?.showAiRoofMeasurement;
      if (typeof nextValue === 'boolean') {
        setShowAiRoofMeasurement(nextValue);
      } else {
        loadSettings();
      }
    };

    window.addEventListener('contractor-settings-updated', handleSettingsUpdated);
    return () => {
      window.removeEventListener('contractor-settings-updated', handleSettingsUpdated);
    };
  }, [location]);

  const allNavigationItems = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboardIcon, feature: 'dashboard' },
    { name: 'Calendar', href: '/calendar', icon: CalendarIcon, feature: 'calendar' },
    { name: 'Clients', href: '/clients', icon: UsersIcon, feature: 'clients' },
    { name: 'Estimates', href: '/estimates', icon: FileTextIcon, feature: 'estimates' },
    { name: 'Invoices', href: '/invoices', icon: BanknoteIcon, feature: 'invoices' },
    { name: 'Projects', href: '/projects', icon: HammerIcon, feature: 'projects' },
    { name: 'Services', href: '/simple-pricing', icon: Settings, feature: 'services' },
    { name: 'AI Roof Measurement', href: '/ai-roof-measurement', icon: BotIcon, feature: 'ai-assistant' as const },
    { name: 'Time Clock', href: '/timeclock', icon: Clock, feature: 'timeclock' },
    { name: 'Agents', href: '/agents', icon: Calculator, feature: 'agents' },
    { name: 'Settings', href: '/settings', icon: SettingsIcon, feature: 'settings' },
  ];

  // Filter by plan, permission, and contractor toggle — AI Roof only shows when enabled in settings
  const navigationItems = allNavigationItems.filter((item) => {
    if (item.feature === 'ai-assistant' && !showAiRoofMeasurement) {
      return false;
    }
    return planAccess.features.includes(item.feature) && canAccessFeature(item.feature);
  });

  const availableItems = navigationItems;

  // Locked features (plan only - permission-restricted items are hidden, not shown as "Coming Soon")
  const lockedFeatures = allNavigationItems.filter(
    (item) => !planAccess.features.includes(item.feature)
  );

  return (
    <div className="remodra-sidebar block flex flex-col">
      {/* Logo - Fixed Header */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-slate-300 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <img 
            src="/remodra-logo.png" 
            alt="Remodra Logo" 
            className="h-8 w-8 rounded-full object-cover remodra-logo-img"
          />
          <span className="text-xl font-bold text-green-500 remodra-logo-text">Remodra</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-slate-500 hover:text-slate-100 hover:bg-slate-700/40"
          onClick={() => setIsCollapsed((prev) => !prev)}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {/* Simple icon using existing SettingsIcon/LogOutIcon set; avoid new import */}
          <span className="text-lg leading-none">
            {isCollapsed ? '›' : '‹'}
          </span>
        </Button>
      </div>

      {/* Navigation - Scrollable */}
      <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto min-h-0">
        {/* Available Features (based on plan) */}
        {availableItems.map((item) => {
          const isActive = location === item.href;
          return (
            <Link
              key={item.name}
              href={item.href}
              className={`remodra-nav-item flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-300 ${
                isActive 
                  ? 'bg-gradient-to-r from-green-500 to-green-700 text-white font-semibold' 
                  : 'text-slate-300 hover:text-green-500 hover:bg-slate-700/50 dark:text-slate-300 dark:hover:text-green-500 dark:hover:bg-slate-700/50 light:text-slate-700 light:hover:text-green-600 light:hover:bg-slate-100'
              }`}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm font-medium remodra-nav-label">{item.name}</span>
            </Link>
          );
        })}


        {/* Locked features — upgrade to unlock */}
        {lockedFeatures.length > 0 && (
          <>
            <Separator className="my-4" />
            <div className="px-3 py-2">
              <h3 className="text-xs font-semibold text-slate-400 dark:text-slate-400 light:text-slate-600 mb-2">Upgrade to unlock</h3>
              {lockedFeatures.map((item) => (
                <div
                  key={item.name}
                  className="flex items-center space-x-3 px-3 py-2 text-slate-500 dark:text-slate-500 light:text-slate-600 rounded-lg"
                >
                  <Lock className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm font-medium">{item.name}</span>
                </div>
              ))}
              <Link href="/billing">
                <Button variant="outline" size="sm" className="mt-2 w-full text-green-600 border-green-500/50 hover:bg-green-500/10">
                  Upgrade plan
                </Button>
              </Link>
            </div>
          </>
        )}
      </nav>

      {/* User Profile - Fixed Footer */}
      <div className="flex-shrink-0 p-4 border-t border-slate-600">
        <div className="flex items-center space-x-3 justify-center">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-green-500 text-slate-900 text-sm font-semibold">
              {getInitials()}
            </AvatarFallback>
          </Avatar>
          {!isCollapsed && (
            <div className="flex-1 min-w-0 remodra-user-block-text">
              <p className="text-sm font-medium text-slate-200 dark:text-slate-200 light:text-slate-800 truncate">
                {typeof user?.firstName === 'string' ? user.firstName : ''} {typeof user?.lastName === 'string' ? user.lastName : ''}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-400 light:text-slate-600 truncate">
                @{typeof user?.username === 'string' ? user.username : user?.email || ''}
              </p>
            </div>
          )}
        </div>
        
        <div className="mt-3 space-y-2">
          {!isCollapsed && (
            <div className="flex items-center justify-start space-x-2">
              <Badge className={`${planAccess.color} text-white text-xs`}>
                {planAccess.name}
              </Badge>
            </div>
          )}
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-2'}`}>
            <ThemeToggle />
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className={`text-slate-400 dark:text-slate-400 light:text-slate-600 hover:text-red-400 hover:bg-red-400/10 ${
                isCollapsed ? 'p-0 w-8 h-8 justify-center' : 'flex-1 justify-start'
              }`}
            >
              <LogOutIcon className="h-4 w-4" />
              {!isCollapsed && <span className="ml-2">Logout</span>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
